/**
 * 시·도 순위 데이터 훅 (DESIGN §8.6.9).
 *
 * 지도 좌우 flank 와 우 패널 표가 **같은 캐시를 공유**한다 — 같은 화면에서
 * 두 곳이 다른 숫자를 보이면 안 되고, 요청도 한 번만 나가야 한다.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { fetchEnvelope, fetchPlain } from '@/lib/api'
import type { Envelope } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

interface RankRow {
  entity_id: string
  n_chargers: number
  availability: number | null
  coverage: number | null
}

interface RankData {
  entity: string
  ranking: RankRow[]
  excluded: RankRow[]
}

interface FilterOptions {
  options: Record<string, { code: string; label: string; n: number | null }[]>
}

export function useSidoRows() {
  const filters = useFilters()

  const { data: names } = useQuery({
    queryKey: ['meta', 'filters'],
    queryFn: ({ signal }) => fetchPlain<FilterOptions>('/meta/filters', signal),
    staleTime: 5 * 60 * 1000,
  })

  // 자기 축은 자기 목록에서 뺀다 — 한 시도를 고르면 목록이 한 줄로 줄어든다
  const listFilters = useMemo(() => ({ ...filters, zcode: [] }), [filters])

  const q = useQuery({
    queryKey: ['metrics/ranking', 'zcode', filterKey(listFilters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<RankData>(
        '/metrics/ranking', toQueryFilters(listFilters), { entity: 'zcode' }, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<RankData> | undefined

  const label = useMemo(() => {
    const m = new Map<string, string>()
    for (const o of names?.options?.zcode ?? []) m.set(o.code, o.label)
    return m
  }, [names])

  const rows = useMemo(() => {
    const rank = (env?.data.ranking ?? []).map((r) => ({ ...r, held: false }))
    const held = (env?.data.excluded ?? []).map((r) => ({ ...r, held: true }))
    return [...rank, ...held].sort((a, b) => b.n_chargers - a.n_chargers)
  }, [env])

  return { rows, label, isLoading: q.isLoading, error: q.error as Error | null }
}

